import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  ChevronLeft,
  Calendar,
  Clock,
  Users,
  Car,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @param {{
 *   vehicle: {
 *     image: string;
 *     provider: string;
 *     vehicle_type: string;
 *     pickup_location: string;
 *     dropoff_location: string;
 *     price: number;
 *   };
 *   bookingData: {
 *     date: Date;
 *     returnDate?: Date | null;
 *     passengers: number;
 *     pickupTime: string;
 *     returnPickupTime?: string;
 *     isRoundTrip: boolean;
 *   };
 *   onBack: () => void;
 *   onConfirm: (data: any) => void;
 *   onReturnToMenu?: () => void;
 * }} props
 */
export default function TransportCheckoutView({
  vehicle,
  bookingData,
  onBack,
  onConfirm,
  onReturnToMenu,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);

  const { date, returnDate, passengers, pickupTime, returnPickupTime, isRoundTrip } = bookingData;

  const outboundAmount = vehicle.price;
  const returnAmount = isRoundTrip ? vehicle.price : 0;
  const totalAmount = outboundAmount + returnAmount;

  const handleConfirm = () => {
    onConfirm({
      vehicle,
      date,
      returnDate: isRoundTrip ? returnDate : null,
      passengers,
      pickupTime,
      returnPickupTime: isRoundTrip ? returnPickupTime : null,
      isRoundTrip,
      totalAmount,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#D4AF37] hover:underline"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Transport
          </Button>
          {onReturnToMenu && (
            <Button
              variant="outline"
              onClick={onReturnToMenu}
              className="gap-2 bg-[#0B2421] hover:bg-[#091E1B] text-white hover:text-white border-none"
            >
              Return to Booking Menu
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-[#0B2421] mb-2">
            Confirm Transport Selection
          </h1>
          <p className="text-gray-500 mb-8">Review your booking details</p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-8 bg-white border border-gray-200 shadow-xl">
              {/* Vehicle Info */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  <img
                    src={vehicle.image}
                    alt={vehicle.provider}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1 text-sm text-[#D4AF37] mb-1">
                    <Car className="h-3 w-3" />
                    <span>{vehicle.provider}</span>
                  </div>
                  <div className="text-xs font-semibold text-gray-500 mb-1">{vehicle.vehicle_type}</div>
                  <div className="flex items-center gap-1 text-sm font-bold text-[#0B2421]">
                    <span>{vehicle.pickup_location}</span>
                    <ArrowRight className="h-3 w-3 text-[#D4AF37]" />
                    <span>{vehicle.dropoff_location}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Date:</span>
                  <span className="font-medium text-[#0B2421] ml-auto">
                    {format(date, "dd MMM yyyy")}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Pickup Time:</span>
                  <span className="font-medium text-[#0B2421] ml-auto">{pickupTime}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Passengers:</span>
                  <span className="font-medium text-[#0B2421] ml-auto">{passengers}</span>
                </div>
                {isRoundTrip && returnDate && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-500">Return Date:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">
                      {format(returnDate, "dd MMM yyyy")}
                    </span>
                  </div>
                )}
                {isRoundTrip && returnPickupTime && (
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-500">Return Pickup:</span>
                    <span className="font-medium text-[#0B2421] ml-auto">{returnPickupTime}</span>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Outbound</span>
                  <span className="font-medium text-[#0B2421]">SGD {outboundAmount.toFixed(2)}</span>
                </div>
                {isRoundTrip && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Return</span>
                    <span className="font-medium text-[#0B2421]">SGD {returnAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200">
                  <span className="text-[#0B2421]">Subtotal</span>
                  <span className="text-[#D4AF37]">SGD {totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-500">
                  💡 Payment will be processed after you complete selecting all services
                </p>
              </div>

              <Button
                onClick={handleConfirm}
                className="w-full h-14 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold text-lg"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Confirm Transport Selection
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
